// Phase 1 — Customer Foundation (Feb 2026).
// Two routes:
//   /dashboard/customers       → CustomerListPage (search, filter, sort)
//   /dashboard/customers/:id   → CustomerProfilePage (profile + order history + notes)
//
// Both pages are tenant-scoped; the backend filters by `company_id` on
// every call. Lifecycle stages: customer | existing_customer | inactive_customer.

import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import DashboardLayout from "../components/DashboardLayout";
import api from "../lib/api";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import { Textarea } from "../components/ui/textarea";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Loader2, RefreshCw, Search, ArrowLeft, MailCheck, MessageCircleMore, Phone, StickyNote, Calculator } from "lucide-react";
import { toast } from "sonner";

// ---------------------------------------------------------------------
// Shared helpers
// ---------------------------------------------------------------------
const LIFECYCLE_LABELS = {
  customer: { label: "Customer", className: "bg-emerald-100 text-emerald-700 border-emerald-200" },
  existing_customer: { label: "Existing", className: "bg-sky-100 text-sky-700 border-sky-200" },
  inactive_customer: { label: "Inactive", className: "bg-amber-100 text-amber-800 border-amber-200" },
};

function LifecycleBadge({ stage, testId }) {
  const cfg = LIFECYCLE_LABELS[stage] || { label: stage || "—", className: "bg-slate-100 text-slate-600" };
  return (
    <Badge variant="outline" className={cfg.className} data-testid={testId}>
      {cfg.label}
    </Badge>
  );
}

function fmtCurrency(v, ccy = "USD") {
  const n = Number(v || 0);
  try {
    return new Intl.NumberFormat("en-US", { style: "currency", currency: ccy }).format(n);
  } catch (_e) {
    return `$${n.toFixed(2)}`;
  }
}

function fmtDate(d) {
  if (!d) return "—";
  try { return new Date(d).toLocaleDateString(); } catch (_e) { return String(d).slice(0, 10); }
}

// =====================================================================
// Customer list page
// =====================================================================
export function CustomerListPage() {
  const nav = useNavigate();
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [stage, setStage] = useState("all");
  const [sort, setSort] = useState("last_order_date");
  const [counts, setCounts] = useState(null);

  const fetchCounts = useCallback(() => api.get("/customers/lifecycle-counts").then(r => setCounts(r.data)).catch(() => {}), []);
  const fetchList = useCallback(async () => {
    setLoading(true);
    try {
      const params = { sort, order: "desc", limit: 200 };
      if (q.trim()) params.q = q.trim();
      if (stage !== "all") params.lifecycle_stage = stage;
      const { data } = await api.get("/customers", { params });
      setItems(data.items || []);
      setTotal(data.total || 0);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to load customers");
    } finally {
      setLoading(false);
    }
  }, [q, stage, sort]);

  useEffect(() => { fetchCounts(); }, [fetchCounts]);
  useEffect(() => { const t = setTimeout(fetchList, 250); return () => clearTimeout(t); }, [fetchList]);

  return (
    <DashboardLayout>
      <div className="space-y-5">
        <div className="flex items-start justify-between flex-wrap gap-3">
          <div>
            <h1 className="font-display text-3xl sm:text-4xl font-semibold" data-testid="customers-page-title">Customers</h1>
            <p className="text-sm text-muted-foreground">Every person who&apos;s bought from you, with lifetime value and full order history.</p>
          </div>
          <Button variant="outline" size="sm" onClick={() => { fetchCounts(); fetchList(); }} disabled={loading} data-testid="customers-refresh-btn">
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
            <span className="ml-2">Refresh</span>
          </Button>
        </div>

        {/* Lifecycle stat strip — also shown on dashboard but mirrored here for context */}
        {counts && (
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3" data-testid="customers-lifecycle-strip">
            {[
              { key: "leads", label: "Leads" },
              { key: "prospects", label: "Prospects" },
              { key: "customers", label: "Customers" },
              { key: "existing_customers", label: "Existing" },
              { key: "inactive_customers", label: "Inactive" },
            ].map(c => (
              <Card key={c.key} className="p-3" data-testid={`customers-stat-${c.key}`}>
                <div className="text-xs text-muted-foreground">{c.label}</div>
                <div className="text-2xl font-semibold">{counts[c.key] ?? 0}</div>
              </Card>
            ))}
          </div>
        )}

        <Card className="p-4">
          <div className="flex flex-wrap items-center gap-3 mb-4">
            <div className="relative flex-1 min-w-[240px] max-w-md">
              <Search className="absolute left-2.5 top-2.5 w-4 h-4 text-muted-foreground" />
              <Input
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Search by name, email, phone, customer code…"
                className="pl-8"
                data-testid="customers-search-input"
              />
            </div>
            <select
              value={stage}
              onChange={(e) => setStage(e.target.value)}
              data-testid="customers-stage-filter"
              className="px-3 py-2 text-sm rounded-lg border border-input bg-background focus:outline-none focus:ring-2 focus:ring-accent/60"
            >
              <option value="all">All stages</option>
              <option value="customer">First-time</option>
              <option value="existing_customer">Existing</option>
              <option value="inactive_customer">Inactive</option>
            </select>
            <select
              value={sort}
              onChange={(e) => setSort(e.target.value)}
              data-testid="customers-sort"
              className="px-3 py-2 text-sm rounded-lg border border-input bg-background focus:outline-none focus:ring-2 focus:ring-accent/60"
            >
              <option value="last_order_date">Sort: last purchase</option>
              <option value="first_order_date">Sort: first purchase</option>
              <option value="lifetime_value">Sort: lifetime value</option>
              <option value="total_orders">Sort: total orders</option>
              <option value="name">Sort: name</option>
            </select>
            <span className="text-xs text-muted-foreground" data-testid="customers-result-count">
              {total} customer{total === 1 ? "" : "s"}
            </span>
          </div>

          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Customer</TableHead>
                <TableHead>Email</TableHead>
                <TableHead>Phone</TableHead>
                <TableHead>Lifecycle</TableHead>
                <TableHead className="text-right">Orders</TableHead>
                <TableHead className="text-right">Lifetime value</TableHead>
                <TableHead>Last purchase</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading && items.length === 0 && (
                <TableRow><TableCell colSpan={7} className="text-center text-sm text-muted-foreground py-6">Loading…</TableCell></TableRow>
              )}
              {!loading && items.length === 0 && (
                <TableRow><TableCell colSpan={7} className="text-center text-sm text-muted-foreground py-6">No customers yet. They&apos;ll appear here after the first order.</TableCell></TableRow>
              )}
              {items.map(c => (
                <TableRow
                  key={c.id}
                  onClick={() => nav(`/dashboard/customers/${c.id}`)}
                  className="cursor-pointer hover:bg-slate-50"
                  data-testid={`customer-row-${c.id}`}
                >
                  <TableCell>
                    <div className="font-medium">{c.name || "(no name)"}</div>
                    <div className="text-[11px] text-muted-foreground font-mono">{c.customer_code}</div>
                  </TableCell>
                  <TableCell className="text-sm">{c.email || "—"}</TableCell>
                  <TableCell className="text-sm">{c.phone || "—"}</TableCell>
                  <TableCell><LifecycleBadge stage={c.lifecycle_stage} testId={`customer-stage-${c.id}`} /></TableCell>
                  <TableCell className="text-right font-mono">{c.total_orders}</TableCell>
                  <TableCell className="text-right font-mono">{fmtCurrency(c.lifetime_value)}</TableCell>
                  <TableCell className="text-sm">{fmtDate(c.last_order_date)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Card>
      </div>
    </DashboardLayout>
  );
}

// =====================================================================
// PILOT-1A — Customer Insights card (display-only derived metrics)
// =====================================================================
const REPEAT_LABEL_STYLES = {
  New:        "bg-slate-100 text-slate-700 border-slate-200",
  Returning:  "bg-sky-100 text-sky-700 border-sky-200",
  Loyal:      "bg-emerald-100 text-emerald-700 border-emerald-200",
  VIP:        "bg-amber-100 text-amber-800 border-amber-200",
};

function CustomerInsightsCard({ customer }) {
  const m = customer?.customer_metrics || {};
  const rate = (typeof m.coupon_redemption_rate === "number")
    ? `${Math.round(m.coupon_redemption_rate * 100)}%`
    : "—";
  const avgGap = (typeof m.average_days_between_orders === "number")
    ? `${m.average_days_between_orders.toFixed(1)} days`
    : "—";
  const repeatLabel = m.repeat_customer_label || "—";
  const repeatStyle = REPEAT_LABEL_STYLES[repeatLabel] || "bg-slate-100 text-slate-700 border-slate-200";
  const favProducts = m.favorite_products || [];
  const favCategories = m.favorite_categories || [];

  return (
    <Card className="p-4" data-testid="customer-insights-section">
      <div className="flex items-center justify-between mb-3">
        <div className="text-sm font-semibold">Customer Insights</div>
        <Badge variant="outline" className={repeatStyle} data-testid="customer-repeat-label">
          {repeatLabel}
        </Badge>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
        <div data-testid="insight-first-purchase">
          <div className="text-xs text-muted-foreground">First purchase</div>
          <div className="font-medium">{m.first_purchase_date ? fmtDate(m.first_purchase_date) : "—"}</div>
        </div>
        <div data-testid="insight-last-purchase">
          <div className="text-xs text-muted-foreground">Last purchase</div>
          <div className="font-medium">{m.most_recent_purchase_date ? fmtDate(m.most_recent_purchase_date) : "—"}</div>
        </div>
        <div data-testid="insight-avg-gap">
          <div className="text-xs text-muted-foreground">Avg days between orders</div>
          <div className="font-medium">{avgGap}</div>
        </div>
        <div data-testid="insight-repeat-score">
          <div className="text-xs text-muted-foreground">Repeat-customer score</div>
          <div className="font-medium">{m.repeat_customer_score ?? "—"}{m.repeat_customer_score === 1 ? " order" : " orders"}</div>
        </div>
        <div data-testid="insight-coupons-issued">
          <div className="text-xs text-muted-foreground">Coupons issued</div>
          <div className="font-medium">{m.total_coupon_issued ?? 0}</div>
        </div>
        <div data-testid="insight-coupons-redeemed">
          <div className="text-xs text-muted-foreground">Coupons redeemed</div>
          <div className="font-medium">{m.total_coupon_redeemed ?? 0}</div>
        </div>
        <div data-testid="insight-redemption-rate">
          <div className="text-xs text-muted-foreground">Redemption rate</div>
          <div className="font-medium">{rate}</div>
        </div>
        <div data-testid="insight-last-coupon-used">
          <div className="text-xs text-muted-foreground">Last coupon used</div>
          <div className="font-medium">{m.last_coupon_used ? fmtDate(m.last_coupon_used) : "—"}</div>
        </div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-4">
        <div data-testid="insight-favorite-products">
          <div className="text-xs text-muted-foreground mb-1.5">Favorite products</div>
          {favProducts.length === 0 ? (
            <div className="text-xs italic text-muted-foreground">No purchases yet.</div>
          ) : (
            <ul className="space-y-1">
              {favProducts.map((p, idx) => (
                <li
                  key={p.product_id || idx}
                  className="text-sm flex items-center justify-between border-b border-slate-100 pb-1"
                  data-testid={`fav-product-${idx}`}
                >
                  <span className="truncate pr-2">{p.product_name || p.product_id}</span>
                  <span className="text-xs text-muted-foreground font-mono">×{p.quantity}</span>
                </li>
              ))}
            </ul>
          )}
        </div>
        <div data-testid="insight-favorite-categories">
          <div className="text-xs text-muted-foreground mb-1.5">Favorite categories</div>
          {favCategories.length === 0 ? (
            <div className="text-xs italic text-muted-foreground">No purchases yet.</div>
          ) : (
            <ul className="space-y-1">
              {favCategories.map((c, idx) => (
                <li
                  key={c.category || idx}
                  className="text-sm flex items-center justify-between border-b border-slate-100 pb-1"
                  data-testid={`fav-category-${idx}`}
                >
                  <span className="truncate pr-2">{c.category}</span>
                  <span className="text-xs text-muted-foreground font-mono">×{c.quantity}</span>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </Card>
  );
}


// =====================================================================
// Customer profile page
// =====================================================================
export function CustomerProfilePage() {
  const { id } = useParams();
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [coupons, setCoupons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [noteDraft, setNoteDraft] = useState("");
  const [saving, setSaving] = useState(false);

  const fetchProfile = useCallback(async () => {
    setLoading(true);
    try {
      const [{ data: profile }, couponRes] = await Promise.all([
        api.get(`/customers/${id}`),
        api.get(`/commerce/customers/${id}/coupons`).catch(() => ({ data: { items: [] } })),
      ]);
      setData(profile);
      setCoupons(couponRes.data?.items || []);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Customer not found");
      nav("/dashboard/customers");
    } finally {
      setLoading(false);
    }
  }, [id, nav]);
  useEffect(() => { fetchProfile(); }, [fetchProfile]);

  const c = data?.customer;
  const orders = data?.orders || [];

  const totals = useMemo(() => {
    if (!c) return null;
    return {
      ltv: c.lifetime_value || 0,
      aov: c.average_order_value || 0,
      total: c.total_orders || 0,
      first: c.first_order_date,
      last: c.last_order_date,
    };
  }, [c]);

  const toggleConsent = async (channel) => {
    if (!c) return;
    const current = c.marketing_consent || {};
    const next = { ...current, [channel]: !current[channel], source: "customer_profile_ui" };
    try {
      const { data: updated } = await api.patch(`/customers/${id}`, { marketing_consent: next });
      setData(prev => ({ ...prev, customer: updated }));
      toast.success(`${channel.toUpperCase()} consent ${next[channel] ? "granted" : "revoked"}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to update consent");
    }
  };

  const addNote = async () => {
    if (!noteDraft.trim()) return;
    setSaving(true);
    try {
      const { data: updated } = await api.patch(`/customers/${id}`, { notes_append: noteDraft.trim() });
      setData(prev => ({ ...prev, customer: updated }));
      setNoteDraft("");
      toast.success("Note added");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to add note");
    } finally { setSaving(false); }
  };

  const recalc = async () => {
    try {
      await api.post(`/customers/${id}/recalculate`);
      await fetchProfile();
      toast.success("Aggregates recalculated");
    } catch (err) { toast.error(err.response?.data?.detail || "Recalculate failed"); }
  };

  if (loading || !c) {
    return (
      <DashboardLayout>
        <div className="py-12 flex items-center justify-center text-sm text-muted-foreground">
          <Loader2 className="w-5 h-5 animate-spin mr-2"/>Loading customer…
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-5" data-testid={`customer-profile-${id}`}>
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="sm" onClick={() => nav("/dashboard/customers")} data-testid="customer-profile-back">
            <ArrowLeft className="w-4 h-4 mr-1"/>Back
          </Button>
          <h1 className="font-display text-2xl sm:text-3xl font-semibold" data-testid="customer-profile-name">
            <span data-testid="customer-name">{c.name || "(no name)"}</span>
          </h1>
          <LifecycleBadge stage={c.lifecycle_stage} testId={`customer-lifecycle-badge`} />
          <span className="font-mono text-xs px-1.5 py-0.5 rounded bg-slate-100 text-slate-700 border border-slate-200" data-testid="customer-code">
            {c.customer_code}
          </span>
          <Button variant="outline" size="sm" onClick={recalc} className="ml-auto" data-testid="customer-recalculate-btn">
            <Calculator className="w-4 h-4 mr-1"/>Recalculate
          </Button>
        </div>

        {/* Stats strip */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3" data-testid="customer-stats-strip">
          <Card className="p-3"><div className="text-xs text-muted-foreground">Lifetime value</div><div className="text-2xl font-semibold" data-testid="customer-stat-ltv">{fmtCurrency(totals.ltv)}</div></Card>
          <Card className="p-3"><div className="text-xs text-muted-foreground">Avg order</div><div className="text-2xl font-semibold" data-testid="customer-stat-aov">{fmtCurrency(totals.aov)}</div></Card>
          <Card className="p-3"><div className="text-xs text-muted-foreground">Total orders</div><div className="text-2xl font-semibold" data-testid="customer-stat-total-orders">{totals.total}</div></Card>
          <Card className="p-3"><div className="text-xs text-muted-foreground">First purchase</div><div className="text-base font-semibold" data-testid="customer-stat-first-order">{fmtDate(totals.first)}</div></Card>
          <Card className="p-3"><div className="text-xs text-muted-foreground">Last purchase</div><div className="text-base font-semibold" data-testid="customer-stat-last-order">{fmtDate(totals.last)}</div></Card>
        </div>

        {/* PILOT-1A — Customer Insights (deterministic derived metrics) */}
        <CustomerInsightsCard customer={c} />

        {/* Contact + consent */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          <Card className="p-4 lg:col-span-2">
            <div className="text-sm font-semibold mb-3">Contact</div>
            <div className="grid grid-cols-2 gap-3 text-sm">
              <div>
                <div className="text-xs text-muted-foreground">Email</div>
                <div className="font-medium" data-testid="customer-email">{c.email || "—"}</div>
              </div>
              <div>
                <div className="text-xs text-muted-foreground">Phone</div>
                <div className="font-medium" data-testid="customer-phone">{c.phone || "—"}</div>
              </div>
              <div>
                <div className="text-xs text-muted-foreground">Tags</div>
                <div className="font-medium">{(c.tags && c.tags.length) ? c.tags.join(", ") : "—"}</div>
              </div>
              <div>
                <div className="text-xs text-muted-foreground">Source channel</div>
                <div className="font-medium">{c.source_channel || "—"}</div>
              </div>
            </div>
          </Card>
          <Card className="p-4">
            <div className="text-sm font-semibold mb-3">Marketing consent</div>
            <div className="space-y-2">
              {[
                { key: "email", icon: <MailCheck className="w-4 h-4"/>, label: "Email" },
                { key: "sms",   icon: <MessageCircleMore className="w-4 h-4"/>, label: "SMS" },
                { key: "calls", icon: <Phone className="w-4 h-4"/>, label: "Calls" },
              ].map(ch => {
                const granted = !!(c.marketing_consent || {})[ch.key];
                return (
                  <div key={ch.key} className="flex items-center justify-between text-sm">
                    <div className="flex items-center gap-2">{ch.icon}<span>{ch.label}</span></div>
                    <Button
                      size="sm"
                      variant={granted ? "default" : "outline"}
                      onClick={() => toggleConsent(ch.key)}
                      data-testid={`consent-toggle-${ch.key}`}
                    >
                      {granted ? "Granted" : "Not granted"}
                    </Button>
                  </div>
                );
              })}
            </div>
          </Card>
        </div>

        {/* Order history */}
        <Card className="p-4">
          <div className="flex items-center justify-between mb-3">
            <div className="text-sm font-semibold">Order history</div>
            <div className="text-xs text-muted-foreground">{orders.length} order{orders.length === 1 ? "" : "s"}</div>
          </div>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Order</TableHead>
                <TableHead>Placed</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Items</TableHead>
                <TableHead className="text-right">Total</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {orders.length === 0 && (
                <TableRow><TableCell colSpan={5} className="text-center text-sm text-muted-foreground py-6">No orders yet.</TableCell></TableRow>
              )}
              {orders.map(o => (
                <TableRow key={o.id} data-testid={`order-row-${o.id}`}>
                  <TableCell>
                    <div className="font-mono text-xs">{o.order_code}</div>
                    {o.is_first_order_for_customer && <Badge variant="outline" className="mt-1 text-[10px] bg-emerald-50">first order</Badge>}
                  </TableCell>
                  <TableCell className="text-sm">{fmtDate(o.placed_at)}</TableCell>
                  <TableCell><Badge variant="outline">{o.status || "—"}</Badge></TableCell>
                  <TableCell className="text-sm">{(o.items || []).length} item{(o.items || []).length === 1 ? "" : "s"}</TableCell>
                  <TableCell className="text-right font-mono">{fmtCurrency(o.total, o.currency)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Card>

        {/* Phase 2A — Coupon history */}
        <Card className="p-4" data-testid="customer-coupons-section">
          <div className="flex items-center justify-between mb-3">
            <div className="text-sm font-semibold">Coupons</div>
            <div className="text-xs text-muted-foreground">{coupons.length} coupon{coupons.length === 1 ? "" : "s"}</div>
          </div>
          {coupons.length === 0 ? (
            <div className="text-xs text-muted-foreground italic">No coupons yet. A post-purchase coupon is generated automatically after eligible orders when the catalog's coupon program is ON.</div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Code</TableHead>
                  <TableHead>Discount</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Created</TableHead>
                  <TableHead>Activates</TableHead>
                  <TableHead>Expires</TableHead>
                  <TableHead>Redeemed</TableHead>
                  <TableHead className="text-right">Saved</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {coupons.map(c => (
                  <TableRow key={c.id} data-testid={`coupon-row-${c.id}`}>
                    <TableCell className="font-mono text-xs">{c.coupon_code}</TableCell>
                    <TableCell>{c.discount_percent}%</TableCell>
                    <TableCell>
                      <Badge variant="outline" className={
                        c.status === "active" ? "bg-emerald-50 text-emerald-700 border-emerald-200" :
                        c.status === "redeemed" ? "bg-sky-50 text-sky-700 border-sky-200" :
                        c.status === "expired" ? "bg-slate-100 text-slate-600" :
                        "bg-amber-50 text-amber-800 border-amber-200"
                      }>{c.status}</Badge>
                    </TableCell>
                    <TableCell className="text-xs">{fmtDate(c.created_at)}</TableCell>
                    <TableCell className="text-xs">{fmtDate(c.activation_date)}</TableCell>
                    <TableCell className="text-xs">{fmtDate(c.expiration_date)}</TableCell>
                    <TableCell className="text-xs">{c.redeemed_at ? fmtDate(c.redeemed_at) : "—"}</TableCell>
                    <TableCell className="text-right font-mono">{c.redeemed_amount ? fmtCurrency(c.redeemed_amount) : "—"}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </Card>

        {/* Notes */}
        <Card className="p-4">
          <div className="text-sm font-semibold mb-3 flex items-center gap-2"><StickyNote className="w-4 h-4"/>Notes</div>
          <div className="space-y-2 mb-3" data-testid="customer-notes-list">
            {(c.notes || []).length === 0 && (
              <div className="text-xs text-muted-foreground italic">No notes yet.</div>
            )}
            {(c.notes || []).map((n, idx) => (
              <div key={idx} className="text-sm border-l-2 pl-3 py-1">
                <div>{n.text}</div>
                <div className="text-[10px] text-muted-foreground">{n.author_email} · {fmtDate(n.created_at)}</div>
              </div>
            ))}
          </div>
          <div className="flex items-end gap-2">
            <Textarea
              rows={2}
              value={noteDraft}
              onChange={(e) => setNoteDraft(e.target.value)}
              placeholder="Add a note about this customer (visible to team members)…"
              data-testid="customer-note-input"
            />
            <Button onClick={addNote} disabled={saving || !noteDraft.trim()} data-testid="customer-add-note-btn">
              {saving ? <Loader2 className="w-4 h-4 animate-spin"/> : "Add note"}
            </Button>
          </div>
        </Card>
      </div>
    </DashboardLayout>
  );
}
